import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AlertTriangle, Bot, Check, ChevronRight, FileText, FolderKanban, Globe, ListChecks, Loader2,
  MessageSquareText, PenLine, Search, Wallet, Wrench,
} from 'lucide-react';
import { currentStep, formatSeconds, formatStepDuration, runSummary, visibleSteps } from './stepModel';

// Ce que Nexus fait pour répondre : en direct, la liste des étapes ; dès qu'il écrit (ou une fois la réponse
// donnée), une ligne repliée « 4 étapes · 12 s » qu'on peut rouvrir.

const KINDS = {
  search: { icon: Search, tone: 'bg-sky-50 text-sky-600 ring-sky-100' },
  read: { icon: FileText, tone: 'bg-indigo-50 text-indigo-600 ring-indigo-100' },
  project: { icon: FolderKanban, tone: 'bg-violet-50 text-violet-600 ring-violet-100' },
  write: { icon: PenLine, tone: 'bg-amber-50 text-amber-600 ring-amber-100' },
  finance: { icon: Wallet, tone: 'bg-emerald-50 text-emerald-600 ring-emerald-100' },
  delegate: { icon: Bot, tone: 'bg-teal-50 text-teal-600 ring-teal-100' },
  web: { icon: Globe, tone: 'bg-cyan-50 text-cyan-600 ring-cyan-100' },
  note: { icon: MessageSquareText, tone: 'bg-slate-50 text-slate-500 ring-slate-200' },
  other: { icon: Wrench, tone: 'bg-slate-50 text-slate-500 ring-slate-200' },
};

const kindOf = (step) => KINDS[step.kind] || KINDS.other;

const StepStatus = ({ step }) => {
  if (step.status === 'running') return <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-400" aria-label="en cours" />;
  if (step.status === 'error') return <AlertTriangle className="h-3.5 w-3.5 text-rose-500" aria-label="échec" />;
  return (
    <span className="flex items-center gap-1 text-slate-400">
      <Check className="h-3.5 w-3.5 text-emerald-500" aria-label="terminé" />
      {formatStepDuration(step.duration)}
    </span>
  );
};

StepStatus.propTypes = { step: PropTypes.object.isRequired };

export const StepRow = ({ step }) => {
  const { icon: Icon, tone } = kindOf(step);
  if (step.kind === 'note') {
    return (
      <li className="flex items-start gap-2.5">
        <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-1 ${tone}`}>
          <Icon className="h-3 w-3" aria-hidden="true" />
        </span>
        <p className="line-clamp-3 text-[13px] italic leading-snug text-slate-600" title={step.label}>{step.label}</p>
      </li>
    );
  }
  return (
    <li className="flex items-start gap-2.5">
      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-1 ${tone}`}>
        <Icon className="h-3 w-3" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`truncate text-[13px] font-medium ${step.status === 'error' ? 'text-rose-700' : 'text-slate-700'}`}>
            {step.label}
          </span>
          <span className="ml-auto shrink-0 text-[11px] tabular-nums"><StepStatus step={step} /></span>
        </div>
        {(step.detail || step.scope) && (
          <p className="truncate text-xs text-slate-500" title={[step.detail, step.scope].filter(Boolean).join(' · ')}>
            {step.detail && <>« {step.detail} »</>}
            {step.scope && (
              <span className="ml-1 rounded-full bg-slate-100 px-1.5 py-px text-[11px] text-slate-600">{step.scope}</span>
            )}
          </p>
        )}
        {step.error && <p className="text-xs text-rose-600">{step.error}</p>}
      </div>
    </li>
  );
};

StepRow.propTypes = { step: PropTypes.object.isRequired };

const useTicker = (active) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
};

const headlineOf = (run) => {
  const running = currentStep(run);
  if (running) return `${running.label}…`;
  if ((run?.steps || []).length && !visibleSteps(run).length) return 'Nexus prépare ses outils…';
  return 'Nexus réfléchit…';
};

const RunSteps = ({ run, live = false, writing = false }) => {
  const [open, setOpen] = useState(false);
  const now = useTicker(live);
  const steps = visibleSteps(run);
  if (!run) return null;

  if (live && !writing) {
    return (
      <div className="rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50/80 to-white px-3 py-2.5 shadow-sm">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-indigo-500" aria-hidden="true" />
          <span className="truncate text-sm font-medium text-indigo-950">{headlineOf(run)}</span>
          <span className="ml-auto text-xs tabular-nums text-indigo-400">{formatSeconds((now - run.startedAt) / 1000)}</span>
        </div>
        {steps.length > 0 && (
          <ol className="mt-2.5 space-y-2 border-t border-indigo-100/70 pt-2.5">
            {steps.slice(-6).map((step) => <StepRow key={step.id} step={step} />)}
          </ol>
        )}
        {steps.length > 6 && (
          <p className="mt-1.5 pl-7 text-[11px] text-indigo-400">+ {steps.length - 6} étapes plus tôt</p>
        )}
      </div>
    );
  }

  if (!steps.length) return null;
  const kinds = [...new Set(steps.filter((step) => step.kind !== 'note').map((step) => step.kind))].slice(0, 4);
  return (
    <div className="mb-1.5">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="group inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-500 shadow-sm transition hover:border-indigo-200 hover:text-indigo-600"
      >
        <ChevronRight className={`h-3 w-3 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden="true" />
        {live ? <Loader2 className="h-3 w-3 shrink-0 animate-spin text-indigo-400" aria-hidden="true" />
          : <ListChecks className="h-3 w-3 shrink-0 text-slate-400 group-hover:text-indigo-500" aria-hidden="true" />}
        <span className="truncate">{live ? `En cours · ${runSummary(run, now)}` : runSummary(run)}</span>
        <span className="ml-0.5 flex -space-x-1">
          {kinds.map((kind) => {
            const { icon: Icon, tone } = KINDS[kind] || KINDS.other;
            return (
              <span key={kind} className={`flex h-4 w-4 items-center justify-center rounded-full ring-1 ${tone}`}>
                <Icon className="h-2.5 w-2.5" aria-hidden="true" />
              </span>
            );
          })}
        </span>
      </button>
      {open && (
        <ol className="mt-2 space-y-2 rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-2.5">
          {steps.map((step) => <StepRow key={step.id} step={step} />)}
        </ol>
      )}
    </div>
  );
};

RunSteps.propTypes = {
  run: PropTypes.shape({ steps: PropTypes.array, startedAt: PropTypes.number, endedAt: PropTypes.number }),
  live: PropTypes.bool,
  writing: PropTypes.bool,
};

export default RunSteps;
